import axios from 'axios';
import { useState, useEffect } from 'react';
import { logError } from '../../../utils/logger/logError';
import {
    InspireRecommendationsAPIUrlResolverProps,
    resolveInspireRecommendationsApiUrl,
} from '../../../utils/urls/resolveInspireRecommendationsApiUrl';
import type { InspireDelivery } from '../../../types/inspire';

interface InspireRecommendationsApiResponse {
    inspireDeliveries: InspireDelivery[];
    endCursor: string;
}

type UseRecommendedInspireDeliveriesAPIArgs = InspireRecommendationsAPIUrlResolverProps;

interface UseRecommendedInspireDeliveriesResult extends InspireRecommendationsApiResponse {
    isLoading: boolean;
    chunksCount: number;
}

/**
 * Recommended Inspire deliveries data.
 */
export const useRecommendedInspireDeliveries = (
    apiArgs: UseRecommendedInspireDeliveriesAPIArgs = {},
    skip = false
): UseRecommendedInspireDeliveriesResult => {
    const url = resolveInspireRecommendationsApiUrl(apiArgs);

    const [result, setResult] = useState<UseRecommendedInspireDeliveriesResult>(() => ({
        inspireDeliveries: [],
        endCursor: '',
        isLoading: true,
        chunksCount: 0,
    }));

    useEffect(() => {
        if (skip) {
            return;
        }

        setResult((state) => ({
            ...state,
            isLoading: true,
        }));

        const controller = new AbortController();

        const fetchInspireDeliveries = async () => {
            try {
                const response = await axios.get<InspireRecommendationsApiResponse>(url, {
                    signal: controller.signal,
                });
                const { inspireDeliveries, endCursor } = response.data;
                setResult((state) => ({
                    inspireDeliveries: [...state.inspireDeliveries, ...inspireDeliveries],
                    endCursor,
                    isLoading: false,
                    chunksCount: state.chunksCount + 1,
                }));
            } catch (error) {
                logError(
                    'useRecommendedInspireDeliveries',
                    `Failed to fetch recommended Inspire deliveries for url: ${url}`,
                    {
                        error,
                    }
                );
                setResult((state) => ({
                    ...state,
                    isLoading: false,
                }));
            }
        };

        fetchInspireDeliveries();

        return () => {
            controller.abort();
        };
    }, [skip, url]);

    return result;
};
